/**
 * Permission Client — the current user's allowed industries for IVT.
 *
 * Calls the IVTHub backend op GetAllowedIndustries (GET /permissions/allowed-industries)
 * through the shared ivtClient (SigV4 execute-api + X-Id-Token) — the SAME auth as every
 * other IVT op. The backend is now the single authority: it resolves the caller's identity
 * from the X-Id-Token (no input), and returns industry codes already de-duped, split
 * (sh_l4 `[,;]`) and sorted. There is no admin bypass — a "see all industries" grant is
 * expressed as an all-industry permission rule, so the frontend always defers to what the
 * backend returns.
 *
 * This replaced the old browser→permission-service direct call: the cross-partition
 * front-door (Midway / @amzn/sentry-fetch / CORS / dataset_id / client-side sh_l4 split)
 * is retired.
 */

import { getIvtClient, GetAllowedIndustriesCommand } from './IvtHubClient';

// Cache keyed by the caller's alias so the two consuming pages (MotionsPage +
// IndustryPipelineOverview) don't each re-request. The alias is used ONLY as this cache
// key — the identity sent to the backend comes from the X-Id-Token, not this value.
let _cache: { alias: string; industries: string[] } | null = null;

/**
 * Result of a permission fetch — a discriminated union so callers can tell apart
 * (fail-CLOSED semantics):
 *   - { ok: true, industries }  — service answered. `industries` MAY be empty
 *     (the user legitimately has NO industry permission → show an empty list).
 *   - { ok: false }             — service unreachable / non-200 / parse error.
 *     Callers must NOT fall open to "all industries" — show an error instead.
 * Distinguishing these is the whole point: an EMPTY allowed set (legit zero
 * permission) must render an empty list, while a FAILED fetch must render an error.
 */
export type AllowedIndustriesResult = { ok: true; industries: string[] } | { ok: false };

/**
 * Fetch the current caller's allowed industries via the IVTHub backend.
 * Returns { ok:true, industries } on success (industries may be empty = the user
 * legitimately has NO permission), or { ok:false } if the request fails — the caller
 * fails CLOSED (error, not open) so an outage can never silently expose every industry.
 *
 * `alias` is used ONLY as the cache key (the two consuming pages call with the current
 * user's uid). The backend derives the real identity from the X-Id-Token, so the alias
 * is never sent — the op takes no input.
 */
export async function fetchAllowedIndustries(alias: string): Promise<AllowedIndustriesResult> {
  if (_cache && _cache.alias === alias) return { ok: true, industries: _cache.industries };

  try {
    const out = await getIvtClient().send(new GetAllowedIndustriesCommand({}));
    // Backend already de-dupes / splits (sh_l4 `[,;]`) / sorts — take the list verbatim.
    // Empty array = zero permission (fail-CLOSED empty list), NOT "no filter = all".
    const industries = out.industries ?? [];
    _cache = { alias, industries };
    return { ok: true, industries };
  } catch {
    // Non-200 / network / parse — fail CLOSED (caller shows an error, not all).
    return { ok: false };
  }
}

export function clearPermissionCache(): void {
  _cache = null;
}

/**
 * Scope a universe of REAL industry codes (from GetSolutionMapAll / ListIndustries)
 * to the user's permission-allowed set — the INTERSECTION.
 *
 * Why an intersection and not "use allowed directly": the allowed set is a permission
 * taxonomy (sh_l4), not the IVT industry list. It contains
 * codes that are NOT real IVT industries (e.g. "CROSS", "GFD AM", "GFD CAC") —
 * feeding those straight into the dropdown creates ghost options that match no
 * Motion (motions.industry is never "CROSS"). Conversely a real industry the user
 * isn't allowed (e.g. "ECOM") must be dropped. Intersecting keeps only codes that
 * are BOTH a real industry AND permitted.
 *
 * fail-CLOSED: `allowed` is the (settled) permitted set — an EMPTY array means the
 * user has NO industry permission, so the intersection is EMPTY (no options), NOT
 * the full universe. `null` is treated the same as empty (defensive — callers pass
 * the settled set only in the ready state; a failed fetch never reaches here, it's
 * gated to an error state upstream). Order follows `universe`.
 */
export function scopeIndustriesToAllowed(
  universe: string[],
  allowed: string[] | null,
): string[] {
  if (!allowed) return []; // no settled set → nothing permitted (fail-closed)
  const allowedSet = new Set(allowed);
  return universe.filter((code) => allowedSet.has(code));
}

/**
 * Resolve the `industries` value to send to a Motion LIST query (ListMotions /
 * ListTaggedPipelines), given the user's explicit filter selection + the SETTLED
 * allowed set (callers only call this in the ready state — a failed permission
 * fetch is gated to an error state upstream and never queries the list).
 *
 * fail-CLOSED:
 *   - explicit selection present → that selection (verbatim).
 *   - empty selection            → the allowed set (`allowed`), EVEN IF EMPTY.
 *     We NEVER return `undefined` ("no filter" = all industries).
 *
 * ⚠️ An empty result (`[]`) MUST NOT be sent to the backend as `industries: []` —
 * an empty `@httpQuery` list serializes to NO query param, which the backend reads
 * as "no filter" and returns EVERY industry (fail-OPEN). Callers must gate on
 * `isEmptyIndustryScope` and short-circuit (render empty, send no request) rather
 * than issue a request with this empty array. See isEmptyIndustryScope.
 *
 * NOTE: the backend is the security boundary. The Motion LIST ops (ListMotions /
 * ListTaggedPipelines) enforce industry permission server-side (P8, commit
 * e77c12e/98d232b): they scope the industry dimension to the caller's permitted set
 * (empty/absent filter → scoped to ALL permitted, never unfiltered; a not-permitted
 * industry → empty). So this client-side scope is now defense-in-depth / UX read-scope
 * narrowing, NOT the only line of defense. The empty-scope short-circuit still matters
 * as a correctness guard (see isEmptyIndustryScope: `industries: []` serializes to no
 * query param, and we avoid an ambiguous request), but a bypass no longer exposes
 * other industries — the server re-verifies.
 */
export function resolveIndustryQueryScope(
  selected: string[],
  allowed: string[],
): string[] {
  if (selected.length > 0) return selected;
  return allowed;
}

/**
 * True when the resolved industry query scope is EMPTY (no explicit selection AND
 * no permitted industry). Callers MUST short-circuit on this — render an empty
 * result and send NO request — instead of sending `industries: []`.
 *
 * WHY (the fail-OPEN trap this guards): an empty `@httpQuery` list serializes to
 * NO query parameter at all (the Smithy query builder emits nothing for a
 * zero-length array). The backend then sees a request with no `industry` filter,
 * indistinguishable from "the caller passed no filter" → it returns EVERY
 * industry. So `industries: []` (intended "empty set → nothing") silently becomes
 * "all industries" on the wire. HTTP query strings cannot express an empty set, so
 * the only correct client behavior for a zero-permission user is to not call the
 * list op at all.
 *
 * This short-circuit is a correctness/UX guard, not the security boundary: the backend
 * enforces industry permission server-side on the LIST ops (P8, commit e77c12e/98d232b),
 * so a zero-permission caller who slipped a request through would get an empty result
 * from the server anyway. We still short-circuit to avoid the ambiguous `industries: []`
 * request (serializes to no query param → reads as "no filter") and to skip a pointless
 * round-trip.
 */
export function isEmptyIndustryScope(selected: string[], allowed: string[] | null): boolean {
  return resolveIndustryQueryScope(selected, allowed ?? []).length === 0;
}
